import { DeleteOutlined, EditOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Link } from '@tanstack/react-router';
import { Button, Form, type FormInstance, Modal, Tag } from 'antd';
import type { ProdutoAdicionalBody } from '@/features/produto-adicional/types/ProdutoAdicional';
import type { ProdutoAdicionalItemBody } from '@/features/produto-adicional/types/ProdutoAdicionalItem';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import { Container } from '@/components/Container';
import { ProdutoAdicionalOrdenacao } from '@/features/produto-adicional/types/enum';
import { orderAdicionaisGrupos } from '@/features/produto-adicional/util/orderAdicionaisGrupos';
import { orderAdicionaisItens } from '@/features/produto-adicional/util/orderAdicionaisItens';
import { ProdutoTabAdicionalListItem } from './ProdutoTabAdicionalListItem';
import { ProdutoTabAdicionalStatus } from './ProdutoTabAdicionalStatus';
import { removeAdicionalGroupForm } from './util';

export function ProdutoTabAdicionalList({
    form,
    adicionarAdicionalItem,
    editarAdicionalItem,
    setFormDirty,
}: {
    form: FormInstance<FormProdutoValues>;
    adicionarAdicionalItem: (adicional: ProdutoAdicionalBody) => void;
    editarAdicionalItem: (adicional: ProdutoAdicionalBody, item: ProdutoAdicionalItemBody) => void;
    setFormDirty: (dirty: boolean) => void;
}) {
    const adicionaisForm = Form.useWatch('adicional', form) || [];
    const adicionaisNaoExcluidos = adicionaisForm.filter((item) => item.excluir !== 'S');
    const adicionais = orderAdicionaisGrupos(adicionaisNaoExcluidos);

    return (
        <Container title='Adicionais'>
            <Container.Content className='flex flex-col items-center justify-center gap-2 xl:flex-row xl:flex-wrap xl:items-start'>
                {adicionais.map((adicional) => {
                    const itensForm = adicional.item || [];
                    const itensNaoExcluidos = itensForm.filter((item) => item.excluir !== 'S');
                    const itens = orderAdicionaisItens(
                        itensNaoExcluidos,
                        adicional.pra_ordenacao || ProdutoAdicionalOrdenacao.OrdemInsercao,
                    );

                    return (
                        <Container
                            className='max-w-[480px] bg-gray-50 sm:w-[480px]'
                            key={adicional.pra_idpk}
                            title={
                                <div className='flex items-center justify-between gap-1 p-2'>
                                    <div className='flex flex-col'>
                                        <div className='flex gap-1 text-base font-semibold'>
                                            {adicional.pra_status ? (
                                                <ProdutoTabAdicionalStatus
                                                    status={adicional.pra_status}
                                                />
                                            ) : null}
                                            {adicional.pra_nome}
                                        </div>

                                        {adicional.pra_detalhes ? (
                                            <div className='text-sm text-gray-500'>
                                                {adicional.pra_detalhes}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className='flex items-center justify-center gap-1'>
                                        <Tag color='gray' variant='solid'>
                                            {adicional.pra_obrigatorio === 'S'
                                                ? 'Obrigatório'
                                                : 'Opcional'}
                                        </Tag>

                                        <Link
                                            rel='noopener noreferrer'
                                            search={{
                                                idpk: adicional.pra_idpk?.toString() || '',
                                                mode: 'editar',
                                                tab1: 'produto',
                                                tab2: 'adicional',
                                            }}
                                            target='_blank'
                                            to='/a/secundario'
                                        >
                                            <Button icon={<EditOutlined />} size='small' />
                                        </Link>

                                        <Button
                                            icon={<DeleteOutlined className='text-red-500' />}
                                            onClick={() => {
                                                Modal.confirm({
                                                    content: `Deseja realmente excluir o grupo "${adicional.pra_nome}"?`,
                                                    onOk: () => {
                                                        removeAdicionalGroupForm(form, adicional);
                                                        setFormDirty(true);
                                                    },
                                                    title: 'Confirmar exclusão',
                                                });
                                            }}
                                            size='small'
                                        />
                                    </div>
                                </div>
                            }
                        >
                            <Container.Content className='flex flex-col gap-1'>
                                {itens.map((item) => (
                                    <ProdutoTabAdicionalListItem
                                        adicional={adicional}
                                        editarAdicionalItem={editarAdicionalItem}
                                        form={form}
                                        item={item}
                                        key={item.key}
                                        setFormDirty={setFormDirty}
                                    />
                                ))}

                                <div className='mt-1 flex items-center justify-center'>
                                    <Button
                                        icon={<PlusCircleOutlined />}
                                        onClick={() => adicionarAdicionalItem(adicional)}
                                    >
                                        Adicionar
                                    </Button>
                                </div>
                            </Container.Content>
                        </Container>
                    );
                })}
            </Container.Content>
        </Container>
    );
}
